<script lang="ts">
  import Button from '../../Button.svelte';
  import Input from '../../Input.svelte';
  import Dropdown from '../../Dropdown.svelte';
  import Notification from '../../Notification.svelte';
  import { CategoryService, type Category } from '../../../lib/api/category.service';
  import { CharityService } from '../../../lib/api/admin/charity.service';
  import { onDestroy, onMount } from 'svelte';
  import { CHARITY_DGR_TAX_OPTIONS } from '../../../lib/charity/dgr-tax-status';
  
  export let open = false;
  export let onClose: () => void;
  export let onSave: (data: any) => void;
  export let charity: any = null; // For edit mode
  
  // Charity fields
  let name = '';
  let abn = '';
  let category_uuid = '';
  let contact_name = '';
  let contact_email = '';
  let contact_phone = '';
  let website = '';
  let head_office_location = '';
  let charity_government_id = '';
  let dgr_tax_status = '';
  let description = '';
  let verified = '0';
  let verified_bsb_no = '';
  let verified_acc_no = '';
  let verified_acc_name = '';
  
  // Categories
  let categories: Category[] = [];
  let loadingCategories = false;
  
  // Form state
  let isEditMode = false;
  let loading = false;
  
  // Notification state
  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';
  
  // Field errors
  let fieldErrors: Record<string, string[]> = {};
  
  // Track if the form has been populated with charity data
  let isFormPopulated = false;

  let logoFile: File | null = null;
  let logoPreview = '';
  let existingLogoUrl = '';
  let logoInput: HTMLInputElement;

  $: currentLogoUrl = logoPreview || existingLogoUrl;

  onMount(async () => {
    await loadCategories();
  });

  onDestroy(() => {
    revokeLogoPreview();
  });
  
  // When charity prop changes (for edit mode)
  $: if (charity && open) {
    isEditMode = true;
    // Only set initial values when first opening in edit mode
    // to avoid overwriting user edits
    if (!isFormPopulated) {
      populateFormWithCharityData(charity);
      isFormPopulated = true;
    }
  } else if (open && !charity) {
    isEditMode = false;
    resetForm();
    isFormPopulated = false;
  }

  // Watch for changes to the open prop
  $: {
    if (open) {
      // If modal is opened, load categories if not already loaded
      if (categories.length === 0) {
        loadCategories();
      }
      
      // If opening in edit mode, populate form if not already done
      if (charity && !isFormPopulated) {
        isEditMode = true;
        populateFormWithCharityData(charity);
        isFormPopulated = true;
      } else if (!charity) {
        isEditMode = false;
        resetForm();
      }
    } else {
      // Reset form population flag when modal is closed
      isFormPopulated = false;
    }
  }
  
  // Function to populate form with charity data
  function populateFormWithCharityData(charityData: any) {
    if (!charityData) return;
    
    name = charityData.name || '';
    abn = charityData.abn || '';
    // Handle category_uuid - can be direct or nested in category object
    category_uuid = charityData.category_uuid || (charityData.category?.uuid || '');
    contact_name = charityData.contact_name || '';
    contact_email = charityData.contact_email || '';
    contact_phone = charityData.contact_phone || '';
    website = charityData.website || '';
    head_office_location = charityData.head_office_location || '';
    charity_government_id = charityData.charity_government_id || '';
    dgr_tax_status = charityData.dgr_tax_status || '';
    description = charityData.description || '';
    // Ensure verified is a string for the select dropdown ("0" or "1")
    const verifiedValue = typeof charityData.verified === 'number'
      ? charityData.verified
      : (charityData.verified === 1 || charityData.verified === '1' ? 1 : 0);
    verified = String(verifiedValue);
    verified_bsb_no = charityData.verified_bsb_no || '';
    verified_acc_no = charityData.verified_acc_no || '';
    verified_acc_name = charityData.verified_acc_name || '';
    existingLogoUrl = charityData.logo || '';
    resetSelectedLogo();
  }

  function revokeLogoPreview() {
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
      logoPreview = '';
    }
  }

  function resetSelectedLogo() {
    logoFile = null;
    revokeLogoPreview();
    if (logoInput) {
      logoInput.value = '';
    }
  }

  function openLogoPicker() {
    logoInput?.click();
  }

  function handleLogoChange(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0] || null;
    delete fieldErrors.logo;
    fieldErrors = { ...fieldErrors };

    if (!file) {
      resetSelectedLogo();
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxBytes = 2 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      fieldErrors = { ...fieldErrors, logo: ['Please upload a JPG, PNG, or WebP logo.'] };
      resetSelectedLogo();
      return;
    }

    if (file.size > maxBytes) {
      fieldErrors = { ...fieldErrors, logo: ['Logo must be smaller than 2 MB.'] };
      resetSelectedLogo();
      return;
    }

    revokeLogoPreview();
    logoFile = file;
    logoPreview = URL.createObjectURL(file);
  }
  
  async function loadCategories() {
    loadingCategories = true;
    try {
      const result = await CategoryService.getCategories();
      if (result.success && result.categories) {
        categories = result.categories;
      } else {
        showNotification('Failed to load categories', 'error');
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
      showNotification('Failed to load categories', 'error');
    } finally {
      loadingCategories = false;
    }
  }
  
  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    notificationType = type;
    notificationShow = true;
  }
  
  function getFieldError(fieldName: string): string | undefined {
    return fieldErrors[fieldName]?.[0];
  }
  
  function validateForm(): boolean {
    fieldErrors = {};
    let isValid = true;
    
    // Required fields based on API validation
    if (!name) {
      fieldErrors.name = ['Charity name is required'];
      isValid = false;
    }
    
    if (!abn) {
      fieldErrors.abn = ['ABN is required'];
      isValid = false;
    } else {
      // Basic ABN validation - remove spaces and check length
      const cleanABN = abn.replace(/\s/g, '');
      if (cleanABN.length !== 11 || !/^\d+$/.test(cleanABN)) {
        fieldErrors.abn = ['ABN must be 11 digits'];
        isValid = false;
      }
    }
    
    // Additional required fields based on API response
    if (!contact_email) {
      fieldErrors.contact_email = ['Contact email is required'];
      isValid = false;
    } else if (!isValidEmail(contact_email)) {
      fieldErrors.contact_email = ['Please enter a valid email address'];
      isValid = false;
    }
    
    if (!contact_phone) {
      fieldErrors.contact_phone = ['Contact phone is required'];
      isValid = false;
    }
    
    if (!head_office_location) {
      fieldErrors.head_office_location = ['Head office location is required'];
      isValid = false;
    }

    if (!dgr_tax_status) {
      fieldErrors.dgr_tax_status = ['Charity tax status is required'];
      isValid = false;
    }

    if (description && description.length > 1000) {
      fieldErrors.description = ['Description cannot exceed 1000 characters'];
      isValid = false;
    }
    
    if (!isEditMode) {
      if (!verified_bsb_no) {
        fieldErrors.verified_bsb_no = ['BSB number is required'];
        isValid = false;
      }
      
      if (!verified_acc_no) {
        fieldErrors.verified_acc_no = ['Account number is required'];
        isValid = false;
      }
      
      if (!verified_acc_name) {
        fieldErrors.verified_acc_name = ['Account name is required'];
        isValid = false;
      }
    }
    
    if (website && !isValidUrl(website)) {
      fieldErrors.website = ['Please enter a valid URL'];
      isValid = false;
    }
    
    return isValid;
  }
  
  function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
  
  function isValidUrl(url: string): boolean {
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  }

  async function handleSubmit() {
    if (!validateForm()) {
      // Show specific field errors in notification
      handleFieldErrors('Please fix the following errors');
      return;
    }
    
    // Prevent duplicate API calls
    if (loading) {
      return;
    }

    loading = true;
    
    const cleanAbn = abn.replace(/\s/g, '');
    const charityData = {
      name,
      abn: cleanAbn,
      category_uuid,
      contact_name: contact_name || undefined,
      contact_email,
      contact_phone,
      website,
      head_office_location,
      charity_government_id: cleanAbn,
      dgr_tax_status,
      description: description || undefined,
      verified: typeof verified === 'string' ? parseInt(verified, 10) : verified,
      verified_bsb_no: isEditMode ? (charity?.verified_bsb_no ?? verified_bsb_no) : verified_bsb_no,
      verified_acc_no: isEditMode ? (charity?.verified_acc_no ?? verified_acc_no) : verified_acc_no,
      verified_acc_name: isEditMode ? (charity?.verified_acc_name ?? verified_acc_name) : verified_acc_name,
    };
    
    try {
      let response;
      
      if (isEditMode && charity?.uuid) {
        response = await CharityService.updateCharity(charity.uuid, charityData);
      } else {
        response = await CharityService.createCharity(charityData);
      }
      
      // Success case
      if (response.success) {
        const savedCharity = Array.isArray(response.data) ? response.data[0] : response.data;
        const charityUuid = savedCharity?.uuid || charity?.uuid;

        if (!isEditMode && logoFile && charityUuid) {
          const logoResponse = await CharityService.updateCharityLogo(charityUuid, logoFile);
          if (!logoResponse.success) {
            showNotification(
              logoResponse.message || 'Charity saved, but the logo could not be uploaded.',
              'error'
            );
            onSave(savedCharity);
            resetForm();
            onClose();
            return;
          }
          resetSelectedLogo();
          onSave(logoResponse.data || savedCharity);
        } else {
          onSave(savedCharity);
        }

        showNotification(isEditMode ? 'Charity updated successfully' : 'Charity added successfully', 'success');
        
        // Close modal immediately to prevent duplicate submissions
        resetForm();
        onClose();
      } 
      // Error case with validation errors
      else if (response.data && Object.keys(response.data).length > 0) {
        console.log("Validation errors:", response.data);
        fieldErrors = response.data.errors;
        handleFieldErrors(response.message || 'Validation failed');
      }
      // General error case
      else {
        showNotification(response.message || 'Failed to save charity', 'error');
      }
    } catch (error) {
      console.error("Error in charity form:", error);
      showNotification('An unexpected error occurred', 'error');
    } finally {
      loading = false;
    }
  }
  
  function handleFieldErrors(message = 'Validation failed') {
    const errorFields = Object.keys(fieldErrors);
    if (errorFields.length > 0) {
      const errorMessages = errorFields.map(field => {
        const fieldName = getFieldDisplayName(field);
        return `${fieldName}: ${fieldErrors[field][0]}`;
      }).slice(0, 3); // Limit to first 3 errors to avoid overly long messages
      
      if (errorFields.length > 3) {
        errorMessages.push(`and ${errorFields.length - 3} more errors`);
      }
      
      showNotification(`${message}: ${errorMessages.join(', ')}`, 'error');
    } else {
      showNotification(message, 'error');
    }
  }

  // Helper function to get display names for fields
  function getFieldDisplayName(field: string): string {
    const fieldNames: Record<string, string> = {
      name: 'Charity Name',
      abn: 'ABN',
      category_uuid: 'Category',
      contact_name: 'Contact Name',
      contact_email: 'Contact Email',
      contact_phone: 'Contact Phone',
      dgr_tax_status: 'Charity Tax Status',
      description: 'Description',
      website: 'Website',
      head_office_location: 'Head Office Location',
      verified_bsb_no: 'BSB Number',
      verified_acc_no: 'Account Number',
      verified_acc_name: 'Account Name'
    };
    
    return fieldNames[field] || field;
  }

  function handleCancel() {
    if (loading) return;
    resetForm();
    onClose();
  }

  function handleWindowKeydown(event: KeyboardEvent) {
    if (!open || loading) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  }
  
  function resetForm() {
    name = '';
    abn = '';
    category_uuid = '';
    contact_name = '';
    contact_email = '';
    contact_phone = '';
    website = '';
    head_office_location = '';
    charity_government_id = '';
    dgr_tax_status = '';
    description = '';
    verified = '0';
    verified_bsb_no = '';
    verified_acc_no = '';
    verified_acc_name = '';
    existingLogoUrl = '';
    resetSelectedLogo();
    fieldErrors = {};
    isFormPopulated = false;
  }
</script>

<svelte:window on:keydown={handleWindowKeydown} />

{#if open}
  <div
    class="modal-overlay"
    role="presentation"
    on:click|self={handleCancel}
  >
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="charity-modal-title">
      <div class="modal-header">
        <h2 id="charity-modal-title">{isEditMode ? 'Edit' : 'Add'} Charity</h2>
        <button
          type="button"
          class="modal-close"
          on:click={handleCancel}
          disabled={loading}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div class="modal-body">
      <div class="form-section">
        {#if !isEditMode}
          <div class="logo-upload-card">
            <div class="logo-preview" class:has-logo={!!currentLogoUrl}>
              {#if currentLogoUrl}
                <img src={currentLogoUrl} alt="{name || 'Charity'} logo preview" />
              {:else}
                <span aria-hidden="true">Logo</span>
              {/if}
            </div>
            <div class="logo-upload-content">
              <label for="admin-charity-logo" class="input-label">Charity logo</label>
              <p class="logo-help">Optional. JPG, PNG, or WebP (max 2 MB).</p>
              <div class="logo-actions">
                <button type="button" class="logo-upload-button" on:click={openLogoPicker} disabled={loading}>
                  {currentLogoUrl ? 'Change logo' : 'Upload logo'}
                </button>
                {#if logoFile}
                  <button type="button" class="logo-clear-button" on:click={resetSelectedLogo}>
                    Remove selected file
                  </button>
                {/if}
              </div>
              <input
                bind:this={logoInput}
                id="admin-charity-logo"
                class="logo-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                on:change={handleLogoChange}
                disabled={loading}
              />
              {#if logoFile}
                <p class="logo-selected">Selected: {logoFile.name}</p>
              {/if}
              {#if getFieldError('logo')}
                <div class="field-error">{getFieldError('logo')}</div>
              {/if}
            </div>
          </div>
        {/if}

        {#if isEditMode}
          <p class="modal-intro">Update organisation details and platform verification. Logo and bank account stay on the profile page.</p>
        {/if}

        <div class="form-panel">
          <h3 class="form-panel-title">Organisation</h3>
        <div class="form-group">
          <Input
            label="Charity Name"
            required={true}
            bind:value={name}
            placeholder="Enter charity name"
            fullWidth={true}
            error={getFieldError('name')}
          />
        </div>
        
        <div class="form-group">
          <Input
            label="ABN"
            required={true}
            bind:value={abn}
            placeholder="Enter ABN (11 digits)"
            fullWidth={true}
            error={getFieldError('abn')}
          />
        </div>
        
        <div class="form-group">
          <label for="category">Category</label>
          <select 
            id="category" 
            bind:value={category_uuid}
            disabled={loadingCategories}
            class="form-select"
            class:error={getFieldError('category_uuid')}
          >
            <option value="">Select category...</option>
            {#each categories as category}
              <option value={category.uuid}>{category.name}</option>
            {/each}
          </select>
          {#if getFieldError('category_uuid')}
            <div class="field-error">{getFieldError('category_uuid')}</div>
          {/if}
        </div>
        
        <div class="form-group">
          <Input
            label="Contact Name"
            bind:value={contact_name}
            placeholder="Enter contact name (optional)"
            fullWidth={true}
            error={getFieldError('contact_name')}
          />
        </div>
        
        <div class="form-group">
          <Input
            label="Contact Email"
            required={true}
            bind:value={contact_email}
            placeholder="Enter contact email"
            fullWidth={true}
            type="email"
            error={getFieldError('contact_email')}
          />
        </div>
        
        <div class="form-group">
          <Input
            label="Contact Phone"
            required={true}
            bind:value={contact_phone}
            placeholder="Enter contact phone"
            fullWidth={true}
            error={getFieldError('contact_phone')}
          />
        </div>
        
        <div class="form-group">
          <Input
            label="Website"
            bind:value={website}
            placeholder="Enter website URL"
            fullWidth={true}
            type="url"
            error={getFieldError('website')}
          />
        </div>

        <div class="form-group">
          <Input
            label="Head Office Location"
            required={true}
            bind:value={head_office_location}
            placeholder="Enter head office location"
            fullWidth={true}
            error={getFieldError('head_office_location')}
          />
        </div>

        <div class="form-group">
          <label for="dgr_tax_status">Charity tax status <span class="required" aria-label="required">*</span></label>
          <select
            id="dgr_tax_status"
            bind:value={dgr_tax_status}
            class="form-select"
            class:error={getFieldError('dgr_tax_status')}
            disabled={loading}
          >
            <option value="">Select tax status...</option>
            {#each CHARITY_DGR_TAX_OPTIONS as option}
              <option value={option.value}>{option.label}</option>
            {/each}
          </select>
          {#if getFieldError('dgr_tax_status')}
            <div class="field-error">{getFieldError('dgr_tax_status')}</div>
          {/if}
        </div>

        <div class="form-group">
          <label for="description">Description</label>
          <textarea
            id="description"
            bind:value={description}
            class="form-textarea"
            class:error={getFieldError('description')}
            placeholder="Short description of the charity (optional)"
            rows="4"
            maxlength="1000"
            disabled={loading}
          ></textarea>
          {#if getFieldError('description')}
            <div class="field-error">{getFieldError('description')}</div>
          {/if}
        </div>
        </div>

        {#if isEditMode}
          <div class="form-panel form-panel-muted">
            <h3 class="form-panel-title">Platform verification</h3>
            <p class="form-panel-hint">Whether this charity is approved to use Leave2Give. Separate from bank phone verification on the profile page.</p>
            <div class="form-group">
              <label for="verified">Charity status on platform</label>
              <select
                id="verified"
                bind:value={verified}
                class="form-select"
                disabled={loading}
              >
                <option value="0">Not verified</option>
                <option value="1">Verified</option>
              </select>
            </div>
          </div>
        {:else}
          <div class="form-panel">
            <h3 class="form-panel-title">Bank details</h3>
            <p class="form-panel-hint">Required when creating a charity. After registration, bank details and phone verification are managed on the charity profile.</p>
            <div class="form-group">
              <Input
                label="BSB Number"
                required={true}
                bind:value={verified_bsb_no}
                placeholder="Enter BSB number"
                fullWidth={true}
                error={getFieldError('verified_bsb_no')}
              />
            </div>
            <div class="form-group">
              <Input
                label="Account Number"
                required={true}
                bind:value={verified_acc_no}
                placeholder="Enter account number"
                fullWidth={true}
                error={getFieldError('verified_acc_no')}
              />
            </div>
            <div class="form-group">
              <Input
                label="Account Name"
                required={true}
                bind:value={verified_acc_name}
                placeholder="Enter account name"
                fullWidth={true}
                error={getFieldError('verified_acc_name')}
              />
            </div>
          </div>
        {/if}
      </div>
      </div>

      <div class="modal-actions">
        <Button
          variant="secondary"
          on:click={handleCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          on:click={handleSubmit}
          disabled={loading || !name || !abn}
        >
          {#if loading}
            <span class="loading-spinner"></span>
          {/if}
          {isEditMode ? 'Save changes' : 'Create charity'}
        </Button>
      </div>
    </div>
  </div>
{/if}

<Notification 
  bind:show={notificationShow}
  type={notificationType}
  message={notificationMessage}
  duration={notificationType === 'error' ? 0 : 3000}
/>

<style>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal {
    background: white;
    border-radius: 12px;
    width: 100%;
    max-width: 560px;
    margin: 1rem;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 40px rgba(52, 58, 64, 0.18);
    overflow: hidden;
  }

  .modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.5rem 1.5rem 0;
    flex-shrink: 0;
  }

  .modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 1rem 1.5rem 0.5rem;
  }

  .modal-intro {
    margin: 0 0 1rem;
    font-size: 0.875rem;
    line-height: 1.5;
    color: #6b7280;
  }

  .form-panel {
    margin-bottom: 1.25rem;
    padding-bottom: 1.25rem;
    border-bottom: 1px solid #f3f4f6;
  }

  .form-panel:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .form-panel-muted {
    background: #faf8f4;
    margin-left: -1.5rem;
    margin-right: -1.5rem;
    padding: 1rem 1.5rem 1.25rem;
    border-bottom: none;
    border-top: 1px solid #f3f4f6;
  }

  .form-panel-title {
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #6b7280;
    margin: 0 0 0.75rem;
  }

  .form-panel-hint {
    margin: -0.25rem 0 0.875rem;
    font-size: 0.8125rem;
    line-height: 1.45;
    color: #6b7280;
  }

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
    text-align: left;
  }

  .modal-close {
    flex-shrink: 0;
    width: 2rem;
    height: 2rem;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #6b7280;
    font-size: 1.5rem;
    line-height: 1;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
  }

  .modal-close:hover:not(:disabled) {
    background: #f3f4f6;
    color: #111827;
  }

  .modal-close:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .form-section {
    margin-bottom: 0;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .form-select {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    background-color: white;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .form-select:focus {
    outline: none;
    border-color: var(--color-primary, #ffb400);
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.12);
  }
  
  .form-select.error {
    border-color: #ef4444;
  }

  .form-textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    background-color: white;
    font-family: inherit;
    resize: vertical;
    min-height: 6rem;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    box-sizing: border-box;
  }

  .form-textarea:focus {
    outline: none;
    border-color: var(--color-primary, #ffb400);
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.12);
  }

  .form-textarea.error {
    border-color: #ef4444;
  }
  
  .field-error {
    color: #ef4444;
    font-size: 0.875rem;
    margin-top: 0.25rem;
  }

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #111827;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.5rem 1.25rem;
    border-top: 1px solid #f3f4f6;
    background: #fff;
    flex-shrink: 0;
  }
  
  .loading-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: #fff;
    animation: spin 1s ease-in-out infinite;
    margin-right: 8px;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .logo-upload-card {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    padding: 1rem;
    margin-bottom: 1rem;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
  }

  .logo-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 5.5rem;
    height: 5.5rem;
    flex-shrink: 0;
    border: 1px dashed #d1d5db;
    border-radius: 12px;
    background: white;
    color: #6b7280;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .logo-preview.has-logo {
    border-style: solid;
  }

  .logo-preview img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 10px;
  }

  .logo-upload-content {
    flex: 1;
    min-width: 0;
  }

  .input-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.25rem;
  }

  .logo-help {
    margin: 0 0 0.75rem 0;
    color: #6b7280;
    font-size: 0.875rem;
    line-height: 1.5;
  }

  .logo-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .logo-upload-button,
  .logo-clear-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0.5rem 1rem;
    border-radius: var(--border-radius-button);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;
  }

  .logo-upload-button {
    border: 1px solid var(--color-primary);
    background: var(--color-primary);
    color: #111827;
  }

  .logo-upload-button:hover:not(:disabled) {
    background: var(--color-primary-hover);
    border-color: var(--color-primary-hover);
  }

  .logo-clear-button {
    border: 1px solid #d1d5db;
    background: white;
    color: #374151;
  }

  .logo-file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .logo-selected {
    margin: 0.5rem 0 0;
    font-size: 0.75rem;
    color: #6b7280;
  }

  @media (max-width: 640px) {
    .modal {
      margin: 0.75rem;
      max-height: calc(100vh - 1.5rem);
    }

    .modal-actions {
      flex-direction: column-reverse;
      align-items: stretch;
    }

    .logo-upload-card {
      flex-direction: column;
      align-items: flex-start;
    }
  }
</style> 